// The main menu, centred over the map: the name and its one line, Continue or Resume when
// there is a game to go back to, and three ways in — Play, Settings, Leaderboard. Play leads
// to the difficulties, one row of a name each; Leaderboard is a holding page until there is
// a server for it.
import { MAPS, type TdMap } from '@thrash/sim';
import { GAME_NAME, TAGLINE } from './brand.ts';
import { button, el, openScreen } from './hud.ts';

export interface MenuActions {
  play(map: TdMap): void;
  /** The game in play, to go back to; null when none. */
  inPlay(): TdMap | null;
  resume(): void;
  /** A game kept in this browser, to carry on with; null when none. */
  saved(): { map: TdMap; wave: number } | null;
  continueSaved(): void;
  settings(anchor: HTMLElement): void;
  help(): void;
  credits(): void;
}

export interface Menu {
  show(): void;
  hide(): void;
  refresh(): void;
  readonly visible: boolean;
}

export function createMenu(a: MenuActions, build?: string): Menu {
  const wrap = el('div');
  wrap.id = 'menu';
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-label', 'Main menu');
  let visible = false;
  let view: 'main' | 'play' = 'main';

  const render = () => {
    wrap.replaceChildren();
    const sheet = el('div', 'menu-sheet');

    if (view === 'main') {
      const masthead = el('div', 'masthead hero');
      masthead.append(el('span', 'kicker', 'Tower defence'), el('h1', '', GAME_NAME), el('p', 'tagline', TAGLINE));
      sheet.appendChild(masthead);

      const playing = a.inPlay();
      const saved = playing ? null : a.saved();
      if (playing || saved) {
        const row = el('div', 'menu-continue');
        const text = el('div', 'row-text');
        if (playing) {
          text.append(el('span', 'kicker', 'In play'), el('b', `diff ${playing.difficulty}`, playing.name), el('span', 'meta', 'paused'));
          row.append(text, button('Resume', () => { hide(); a.resume(); }, { cls: 'primary', icon: 'play', key: 'Enter' }));
        } else if (saved) {
          text.append(el('span', 'kicker', 'Saved game'), el('b', `diff ${saved.map.difficulty}`, saved.map.name), el('span', 'meta', `wave ${Math.max(1, saved.wave)}`));
          row.append(text, button('Continue', () => { hide(); a.continueSaved(); }, { cls: 'primary', icon: 'play', key: 'Enter' }));
        }
        sheet.appendChild(row);
      }

      const opts = el('div', 'menu-opts');
      const settingsBtn = button('Settings', () => a.settings(settingsBtn), { icon: 'settings' });
      opts.append(
        button('Play', () => { view = 'play'; render(); }, { cls: 'primary', icon: 'play', ...(playing || saved ? {} : { key: 'Enter' }) }),
        settingsBtn,
        button('Leaderboard', () => openScreen('Leaderboard', leaderboardBody), { icon: 'trophy' }),
      );
      sheet.appendChild(opts);
    } else {
      const head = el('div', 'menu-play-head');
      head.append(button('Back', () => { view = 'main'; render(); }, { cls: 'link', icon: 'back' }), el('div', 'ph', 'Difficulty'));
      sheet.appendChild(head);

      const list = el('div', 'map-list');
      for (const m of MAPS) {
        const row = el('div', 'map-row');
        const text = el('div', 'row-text');
        text.appendChild(el('b', `map-name diff ${m.difficulty}`, m.name));
        row.append(text, button('Play', () => { hide(); a.play(m); }, { cls: 'primary' }));
        list.appendChild(row);
      }
      sheet.appendChild(list);
    }

    const foot = el('div', 'menu-foot');
    foot.appendChild(button('Help', () => a.help(), { cls: 'link', key: '?' }));
    foot.appendChild(button('Credits', () => a.credits(), { cls: 'link' }));
    if (build) foot.appendChild(el('span', 'build', `build ${build.slice(0, 12)}`));
    sheet.appendChild(foot);
    wrap.appendChild(sheet);
  };

  /** The leaderboard's holding page: a short panel, until the server that would fill it exists. */
  const leaderboardBody = (): HTMLElement => {
    const box = el('div', 'leaderboard');
    box.append(
      el('span', 'kicker', 'Leaderboard'),
      el('p', '', 'Global leaderboards land with the server. Until then the most waves held at each difficulty is kept in this browser.'),
    );
    return box;
  };

  function show(): void {
    view = 'main';
    if (visible) { render(); return; }
    visible = true;
    render();
    document.body.appendChild(wrap);
    document.body.classList.add('menu-open');
  }

  function hide(): void {
    if (!visible) return;
    visible = false;
    wrap.remove();
    document.body.classList.remove('menu-open');
  }

  return { show, hide, refresh: () => { if (visible) render(); }, get visible() { return visible; } };
}